import {
  ensureWidgetSendHistoryMessageIds,
  greetingPhrasesToSendHistory,
  isWidgetMessageId,
  type ProactiveGreetingPage,
  type ProactiveGreetingPhrase,
  type WidgetSendHistoryItem,
} from '@teo/widget-shared';

export type OutboundQueueEntryKind = 'text' | 'image';

export type OutboundQueueEntry = {
  clientId: string;
  /** Defaults to `text` when omitted (backward compatible). */
  kind?: OutboundQueueEntryKind;
  text: string;
  threadId: number | string | null;
  createdAt: string;
  /** Greeting offer import for `POST /api/widget/send` (`history`). */
  history?: WidgetSendHistoryItem[];
  greetingId?: string;
  /** Page snapshot captured at original send time (first send on pathname). */
  page?: ProactiveGreetingPage;
  /** Stable send id for `POST /api/widget/send` (`message_id`). */
  messageId?: string;
  /** @deprecated Legacy queue rows — migrated to `history` on read. */
  aiMessages?: ProactiveGreetingPhrase[];
  /** Image: original file name for restore / upload. */
  fileName?: string;
  /** Image: MIME type hint when restoring from IndexedDB. */
  mimeType?: string;
  /** Image: upload succeeded; `text` holds the server URL — only send step remains. */
  uploadedUrl?: boolean;
};

const STORAGE_PREFIX = 'teo_widget_outbound_queue';

function storageKey(scopeKey: string): string {
  return `${STORAGE_PREFIX}:${scopeKey}`;
}

function readRaw(scopeKey: string): OutboundQueueEntry[] {
  if (typeof localStorage === 'undefined') return [];

  try {
    const raw = localStorage.getItem(storageKey(scopeKey));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeOutboundQueueEntry).filter(isValidEntry);
  } catch {
    return [];
  }
}

function writeRaw(scopeKey: string, entries: OutboundQueueEntry[]): void {
  if (typeof localStorage === 'undefined') return;

  try {
    if (entries.length === 0) {
      localStorage.removeItem(storageKey(scopeKey));
      return;
    }
    localStorage.setItem(storageKey(scopeKey), JSON.stringify(entries));
  } catch {
    // ignore quota / private mode
  }
}

const GREETING_PAGE_TYPES = new Set(['home', 'category', 'product', 'other']);

function isValidPage(value: unknown): value is ProactiveGreetingPage {
  if (!value || typeof value !== 'object') return false;
  const page = value as Record<string, unknown>;
  return (
    typeof page.type === 'string' &&
    GREETING_PAGE_TYPES.has(page.type) &&
    typeof page.url === 'string' &&
    typeof page.pathname === 'string' &&
    (page.title === undefined || typeof page.title === 'string') &&
    (page.description === undefined || typeof page.description === 'string') &&
    (page.product === undefined ||
      (typeof page.product === 'object' && page.product !== null))
  );
}

function normalizeOutboundQueueEntry(value: unknown): unknown {
  if (!value || typeof value !== 'object') return value;
  const row = value as Record<string, unknown>;
  let next: Record<string, unknown> = row;

  if (row.page !== undefined && !isValidPage(row.page)) {
    next = { ...row };
    delete next.page;
  }

  if (next.history !== undefined) {
    const greetingId =
      typeof next.greetingId === 'string' ? next.greetingId : undefined;
    return {
      ...next,
      history: ensureWidgetSendHistoryMessageIds(
        next.history as WidgetSendHistoryItem[],
        { greetingId },
      ),
    };
  }

  const legacy = next.aiMessages;
  if (!Array.isArray(legacy) || legacy.length === 0) return next;

  const createdAt =
    typeof next.createdAt === 'string'
      ? Date.parse(next.createdAt)
      : Date.now();

  return {
    ...next,
    history: greetingPhrasesToSendHistory(legacy as ProactiveGreetingPhrase[], {
      baseTimeMs: Number.isFinite(createdAt) ? createdAt : Date.now(),
      greetingId:
        typeof next.greetingId === 'string' ? next.greetingId : undefined,
    }),
  };
}

function isValidHistory(value: unknown): value is WidgetSendHistoryItem[] {
  if (!Array.isArray(value)) return false;
  return value.every(
    (item) =>
      item &&
      typeof item === 'object' &&
      typeof (item as WidgetSendHistoryItem).role_id === 'number' &&
      typeof (item as WidgetSendHistoryItem).type === 'string' &&
      typeof (item as WidgetSendHistoryItem).payload === 'string' &&
      typeof (item as WidgetSendHistoryItem).datetime === 'string' &&
      typeof (item as WidgetSendHistoryItem).message_id === 'string' &&
      isWidgetMessageId((item as WidgetSendHistoryItem).message_id),
  );
}

function isValidEntry(value: unknown): value is OutboundQueueEntry {
  if (!value || typeof value !== 'object') return false;
  const row = value as Record<string, unknown>;
  const kind = row.kind;
  if (
    kind !== undefined &&
    kind !== 'text' &&
    kind !== 'image'
  ) {
    return false;
  }
  return (
    typeof row.clientId === 'string' &&
    typeof row.text === 'string' &&
    typeof row.createdAt === 'string' &&
    (row.threadId === null ||
      typeof row.threadId === 'string' ||
      typeof row.threadId === 'number') &&
    (row.fileName === undefined || typeof row.fileName === 'string') &&
    (row.mimeType === undefined || typeof row.mimeType === 'string') &&
    (row.uploadedUrl === undefined || typeof row.uploadedUrl === 'boolean') &&
    (row.history === undefined || isValidHistory(row.history)) &&
    (row.greetingId === undefined || typeof row.greetingId === 'string') &&
    (row.messageId === undefined || typeof row.messageId === 'string') &&
    (row.page === undefined || isValidPage(row.page))
  );
}

export function readOutboundQueue(scopeKey: string): OutboundQueueEntry[] {
  return readRaw(scopeKey);
}

export function getOutboundQueueSize(scopeKey: string): number {
  return readRaw(scopeKey).length;
}

export function enqueueOutboundMessage(
  scopeKey: string,
  entry: OutboundQueueEntry,
): OutboundQueueEntry[] {
  const current = readRaw(scopeKey);
  if (current.some((row) => row.clientId === entry.clientId)) {
    return current;
  }
  const next = [...current, entry];
  writeRaw(scopeKey, next);
  return next;
}

export function updateOutboundMessage(
  scopeKey: string,
  clientId: string,
  patch: Partial<OutboundQueueEntry>,
): OutboundQueueEntry[] {
  const current = readRaw(scopeKey);
  const index = current.findIndex((row) => row.clientId === clientId);
  if (index < 0) return current;
  const next = [...current];
  next[index] = { ...next[index]!, ...patch, clientId };
  writeRaw(scopeKey, next);
  return next;
}

export function removeOutboundMessage(
  scopeKey: string,
  clientId: string,
): OutboundQueueEntry[] {
  const next = readRaw(scopeKey).filter((row) => row.clientId !== clientId);
  writeRaw(scopeKey, next);
  return next;
}

export function clearOutboundQueue(scopeKey: string): void {
  writeRaw(scopeKey, []);
}

export function resolveOutboundQueueScopeKey(
  visitorId?: string | null,
  companyId?: string | number | null,
): string {
  const visitor = visitorId?.trim() || 'anonymous';
  const company = companyId != null ? String(companyId) : 'default';
  return `${company}:${visitor}`;
}

export function isImageQueueEntry(entry: OutboundQueueEntry): boolean {
  return entry.kind === 'image' || entry.uploadedUrl === true;
}
